import type { Product, Category } from '../types/Product';
import { API_CONFIG, API_ENDPOINTS, buildApiUrl } from '../config/api.config';
import { apiClient } from './apiClient';

// Backend response wrapper
export interface ResultEntity<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string | {
    error: string;
    error_code?: string;
  };
  statusCode?: number;
  code?: number;
}

// Error response type
export interface ApiError {
  message: string;
  statusCode?: number;
  error?: string;
}

// Default headers for all requests
const getDefaultHeaders = (includeAuth: boolean = false, method?: string): HeadersInit => {
  const headers: Record<string, string> = {
    'x-tenant-key': API_CONFIG.TENANT_KEY,
  };
  
  // Only set Content-Type for requests that have a body
  if (method !== 'DELETE' && method !== 'GET') {
    headers['Content-Type'] = 'application/json';
  }
  
  if (includeAuth) {
    const token = localStorage.getItem('auth_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  
  return headers;
};

// Generic API request handler
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  includeAuth: boolean = false
): Promise<T> {
  const url = buildApiUrl(endpoint);
  const method = options.method || 'GET';
  
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...getDefaultHeaders(includeAuth, method),
        ...options.headers,
      },
    });
    
    const result: ResultEntity<T> = await response.json();
    
    if (!response.ok || !result.success) {
      // Handle nested error structure from backend
      let errorMessage = 'Request failed';
      if (result.error) {
        if (typeof result.error === 'object' && result.error.error) {
          errorMessage = result.error.error;
        } else if (typeof result.error === 'string') {
          errorMessage = result.error;
        }
      } else if (result.message) {
        errorMessage = result.message;
      }
      throw new Error(errorMessage);
    }
    
    return result.data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
}

export const api = {
  async getProducts(): Promise<Product[]> {
    try {
      return await apiRequest<Product[]>(API_ENDPOINTS.PRODUCTS.BASE);
    } catch (error) {
      // Return empty array on error for backward compatibility
      return [];
    }
  },

  async getProductsByCategory(categorySlug: string): Promise<Product[]> {
    try {
      return await apiRequest<Product[]>(
        `${API_ENDPOINTS.PRODUCTS.BASE}?category=${categorySlug}`
      );
    } catch (error) {
      return [];
    }
  },

  async getFeaturedProducts(): Promise<Product[]> {
    try {
      return await apiRequest<Product[]>(
        `${API_ENDPOINTS.PRODUCTS.BASE}?tags=featured&status=active&inStock=true&limit=8`
      );
    } catch (error) {
      return [];
    }
  },

  async getNewArrivals(): Promise<Product[]> {
    try {
      return await apiRequest<Product[]>(
        `${API_ENDPOINTS.PRODUCTS.BASE}?sortBy=newest&limit=10`
      );
    } catch (error) {
      return [];
    }
  },

  async getCategories(): Promise<Category[]> {
    try {
      return await apiRequest<Category[]>(API_ENDPOINTS.CATEGORIES.BASE);
    } catch (error) {
      return [];
    }
  },

  async addToCart(productId: string, quantity: number = 1): Promise<boolean> {
    try {
      // TODO: Implement backend cart API when available
      // For now, we'll store it in localStorage
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      const existingItem = cart.find((item: any) => item.productId === productId);
      
      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        cart.push({ productId, quantity });
      }
      
      localStorage.setItem('cart', JSON.stringify(cart));
      return true;
    } catch (error) {
      return false;
    }
  },

  async getProduct(id: string): Promise<Product | null> {
    try {
      return await apiRequest<Product>(API_ENDPOINTS.PRODUCTS.BY_ID(id));
    } catch (error) {
      return null;
    }
  },
  
  // Add upload method for convenience
  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    return apiClient.upload<T>(endpoint, formData, true);
  }
};

// Export the new API client
export { apiClient } from './apiClient';